import { useTranslation } from 'react-i18next';
import { matchRoute, useLocation } from '../../app/router';
import { DashboardLayout } from './DashboardLayout';
import { FavoritesPage, HomePage, ProjectPage, RecentPage, SearchPage } from './pages';
import { useOrg } from './queries';
import { ServiceUnavailable } from './ServiceUnavailable';
import { SharedPage } from './SharedPage';
import { TrashPage } from './TrashPage';
import { TemplatesPage } from '../templates/TemplatesPage';
import { AuditLogPage } from '../settings/AuditLogPage';
import { SettingsPage } from '../settings/SettingsPage';
import { PageSkeleton } from '../../ui/states';

/** Signed-in area outside the editor: picks the page from the current route. */
export function Dashboard() {
  const { t } = useTranslation();
  const { path, search } = useLocation();
  const route = matchRoute(path, search);
  const { org, isPending, isError, refetch } = useOrg();

  let page;
  if (isPending) page = <PageSkeleton label={t('auth.loading')} />;
  // Unreadable is not "no organization": an outage must not show a member the externals' page (P12a).
  else if (isError && !org) page = <ServiceUnavailable onRetry={() => void refetch()} />;
  else if (!org) {
    // Externals (clients, freelancers) have no organization: what is shared with them, their projects, settings.
    switch (route?.name) {
      case 'project':
      case 'folder':
        page = <ProjectPage projectId={route.params.projectId!} folderId={route.params.folderId} />;
        break;
      case 'settings':
      case 'settingsSection':
        page = <SettingsPage section={route.params.section} />;
        break;
      default:
        page = <SharedPage orgless />;
    }
  } else {
    switch (route?.name) {
      case 'shared':
        page = <SharedPage />;
        break;
      case 'project':
      case 'folder':
        page = <ProjectPage projectId={route.params.projectId!} folderId={route.params.folderId} />;
        break;
      case 'settings':
      case 'settingsSection':
        page = <SettingsPage section={route.params.section} />;
        break;
      case 'audit':
        page = <AuditLogPage org={org} />;
        break;
      case 'trash':
        page = <TrashPage org={org} />;
        break;
      case 'favorites':
        page = <FavoritesPage />;
        break;
      case 'recent':
        page = <RecentPage />;
        break;
      case 'templates':
        page = <TemplatesPage org={org} />;
        break;
      case 'search':
        page = <SearchPage q={route.query.get('q') ?? ''} />;
        break;
      default:
        page = <HomePage org={org} />;
    }
  }
  return <DashboardLayout>{page}</DashboardLayout>;
}
